// =============================================================================
// ApiClientError — BE 의 표준 에러 envelope 을 wrap 한 FE 측 Error 클래스.
//
// docs/designs/error-format.md §0/§5.6 의 결정.
//
// 사용:
//   try {
//     await api.post('/users', dto);
//   } catch (e) {
//     if (e instanceof ApiClientError) {
//       if (e.code === 'AUTH_INVALID_CREDENTIALS') showLoginError();
//       else if (e.code === 'VALIDATION_FAILED') applyFieldErrors(e.fields);
//     }
//   }
//
// 본 클래스는 axios response 인터셉터 (`createApiErrorInterceptor`) 가
// `error.response.data.error` 를 발견했을 때 wrap 하여 reject 한다.
// network 오류 / timeout / non-envelope 응답은 wrap 하지 않고 원본 AxiosError
// 그대로 reject — 호출부가 axios 표준 방식으로 검사 가능.
// =============================================================================

import type { AxiosError } from 'axios';
import type { ApiError, ApiErrorDetails } from '@axsolutionsdev/shared';
import { isApiErrorResponse } from '@axsolutionsdev/shared';

export class ApiClientError extends Error {
  /** 표준 에러 코드 (예: `AUTH_INVALID_CREDENTIALS`). */
  readonly code: string;
  /** HTTP status. axios response 에서 추출. */
  readonly status: number;
  /** envelope 의 details (검증 필드, lockedUntil 등). */
  readonly details: ApiErrorDetails | undefined;
  /** envelope 의 traceId. 사용자 신고 시 운영 측 로그 검색 키로 활용. */
  readonly traceId: string | undefined;
  /** 원본 AxiosError (필요 시 status text / config 검사). */
  override readonly cause: AxiosError | undefined;

  constructor(payload: ApiError, status: number, cause?: AxiosError) {
    super(payload.message);
    this.name = 'ApiClientError';
    this.code = payload.code;
    this.status = status;
    this.details = payload.details;
    this.traceId = payload.traceId;
    this.cause = cause;
  }

  /** 검증 에러의 필드별 메시지 — `details.fields` shortcut. */
  get fields(): NonNullable<ApiErrorDetails['fields']> | undefined {
    return this.details?.fields;
  }

  /**
   * `error.response.data` 가 우리 envelope 이면 ApiClientError 로 wrap 해 반환.
   * 아니면 undefined — 호출부는 원본 AxiosError 를 그대로 reject 하면 된다.
   */
  static fromAxios(err: AxiosError): ApiClientError | undefined {
    const body = err.response?.data;
    if (!isApiErrorResponse(body)) return undefined;
    const status = err.response?.status ?? 500;
    return new ApiClientError(body.error, status, err);
  }
}
